import { planBillChange, planDiscard, type ReleaseStep } from "../promotions/release";
import type { PartnerDirectory } from "../promotions/partners/port";
import type { SaleEffect } from "./registry";

/**
 * The promotion effects of sale effects v2 (docs/architecture/SALE_EFFECTS.md §5, §6 E1a; DISCOUNT_V2_PLAN.md
 * §9.10 decisions 32, 60 to 65): what a write must give back, and the customer whose loyalty record to load.
 * Each is a pure "changes → plan"; the executor (E1b) runs the plan over the ports.
 */

export interface PromotionEffectContext {
  /** The merchant's partners: which entry is the points line, which partner a coupon belongs to. */
  directory: PartnerDirectory;
}

/**
 * Give back what the bill no longer stands behind: the release on a told discard, the points refund and the
 * DotPe release on a bill change. Replaces v1's `resetSaleEffect`, `loyaltyRefundEffect` and
 * `dotpeOfferReleaseEffect`.
 *
 * - **`discarded`**: `planDiscard` on the sale the partners know, which is the PREVIOUS one. The write that
 *   throws a cart away either replaces it (a new or no sale) or empties it under the same `mid`, and in both
 *   the thing to release is what the previous write left on the bill; v1 read the old state too. Only when
 *   there is no previous sale is `next` used, so a told discard of a sale seen for the first time still
 *   releases. A discard is never also planned as a bill change: v1 web did both on an emptied cart and
 *   refunded one line twice (decision 61).
 * - **`placed`**: nothing. A placed order keeps its points and coupons (v1 guarded this with the placed-order
 *   marker `getLastOrderPlacedMid`), and the write that saves it may well change the bill.
 * - otherwise **a bill change**: `planBillChange`, which itself ignores a switched sale, an API sale and a
 *   write that `keepsPromotions`.
 *
 * An empty plan is null: there is nothing to do.
 */
export const promotionReleaseEffect: SaleEffect<ReleaseStep[], PromotionEffectContext, "promotionRelease"> = {
  id: "promotionRelease",
  reads: ["billChangedForPromotions", "discarded"],
  plan({ previous, next, intent, changes, context }) {
    const told = (kind: "discarded" | "placed") => changes.some((change) => change.kind === kind);
    if (told("placed")) return null;
    const steps = told("discarded")
      ? planDiscard(previous ?? next, { directory: context.directory })
      : planBillChange(previous, next, { directory: context.directory, keepsPromotions: intent.keepsPromotions });
    return steps.length > 0 ? steps : null;
  },
};

/** Load the new customer's loyalty record (v1's `customerLoyaltyFetchEffect`, `ensureCustomerLoyaltyInfo`). */
export type CustomerLoyaltyFetchPlan = { fetchLoyaltyRecordFor: string };

export const customerLoyaltyFetchEffect: SaleEffect<CustomerLoyaltyFetchPlan, unknown, "customerLoyaltyFetch"> = {
  id: "customerLoyaltyFetch",
  reads: ["customerChanged"],
  plan({ changes }) {
    const change = changes.find((candidate) => candidate.kind === "customerChanged");
    return change?.kind === "customerChanged" ? { fetchLoyaltyRecordFor: change.to } : null;
  },
};

/** E1's registry, in v1's order: the release first, then the customer fetch. */
export const PROMOTION_EFFECTS = [promotionReleaseEffect, customerLoyaltyFetchEffect] as const;
